import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Stack, Alert, CircularProgress, Chip, useTheme, Select, MenuItem, FormControl, InputLabel, IconButton, Autocomplete } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { isAdminOrSuperAdmin, fetchAdminAccessData } from '../config/adminEmails';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

interface MatchScore {
  lapang?: string | number;
  teamA?: string;
  scoreA?: string | number;
  teamB?: string;
  scoreB?: string | number;
  status?: string;
  matchId?: string; // 5-digit random ID for unique identification
  // Google Sheets properties lama (hapus jika sudah tidak dipakai)
  // Lapang?: string | number;
  // 'Team A'?: string;
  // 'Team B'?: string;
  // Score?: string | number;
  // Status?: string;
}

interface CourtData {
  teamA: string;
  teamB: string;
  scoreA: number;
  scoreB: number;
  live: boolean;
  status: string;
  matchId?: string; // 5-digit random ID for unique identification
  lockedBy?: string | null;
}

interface LiveScoreBoxProps {
  court: number;
  teamA: string;
  teamB: string;
  scoreA: number;
  scoreB: number;
  setTeamA: (v: string) => void;
  setTeamB: (v: string) => void;
  onStart: () => void;
  onSave: () => void;
  onReset: () => void;
  onScoreAChange: (newScore: number) => void;
  onScoreBChange: (newScore: number) => void;
  live: boolean;
  status: string;
  small?: boolean;
  loading?: boolean;
  loadingLiveScore?: boolean;
  isAdmin: boolean;
  isLocked?: boolean;
  lockedBy?: string | null;
  currentAdminEmail?: string | null;
  playerNames?: string[]; // 🚀 NEW: Player names for autocomplete
  loadingPlayerNames?: boolean; // 🚀 NEW: Loading state for player names
}

const LiveScoreBox: React.FC<LiveScoreBoxProps> = (props) => {
  const { court, teamA, teamB, scoreA, scoreB, setTeamA, setTeamB, onStart, onSave, onReset, onScoreAChange, onScoreBChange, live, status, small, loading, loadingLiveScore, isAdmin, playerNames, loadingPlayerNames } = props;
  const theme = useTheme();
  // Kembalikan warna skor ke style sebelumnya
  // Untuk judul, jika mode terang, pakai warna putih
  const titleColor = theme.palette.mode === 'light' ? theme.palette.primary.main : '#fff';

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Swap teams state (visual only)
  const [isSwapped, setIsSwapped] = useState(false);

  // Touch handling for swipe gestures
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  // Mouse handling for drag gestures
  const [mouseStart, setMouseStart] = useState<{ x: number; y: number } | null>(null);
  const [mouseEnd, setMouseEnd] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<'A' | 'B' | null>(null);
  
  // Use refs for values that change frequently to avoid re-adding event listeners
  const mouseStartRef = useRef(mouseStart);
  const mouseEndRef = useRef(mouseEnd);
  const isDraggingRef = useRef(isDragging);
  const currentTeamRef = useRef(currentTeam);
  const scoreARef = useRef(scoreA);
  const scoreBRef = useRef(scoreB);
  const isAdminRef = useRef(isAdmin);
  const statusRef = useRef(status);
  const loadingRef = useRef(loading);
  
  // Update refs when state changes
  useEffect(() => {
    mouseStartRef.current = mouseStart;
    mouseEndRef.current = mouseEnd;
    isDraggingRef.current = isDragging;
    currentTeamRef.current = currentTeam;
    scoreARef.current = scoreA;
    scoreBRef.current = scoreB;
    isAdminRef.current = isAdmin;
    statusRef.current = status;
    loadingRef.current = loading;
  }, [mouseStart, mouseEnd, isDragging, currentTeam, scoreA, scoreB, isAdmin, status, loading]);

  // Determine which team to display on left and right based on swap state
  const leftTeam = isSwapped ? 'B' : 'A';
  const rightTeam = isSwapped ? 'A' : 'B';
  const leftTeamName = leftTeam === 'A' ? teamA : teamB;
  const rightTeamName = rightTeam === 'A' ? teamA : teamB;
  const leftScore = leftTeam === 'A' ? scoreA : scoreB;
  const rightScore = rightTeam === 'A' ? scoreA : scoreB;
  const leftColor = leftTeam === 'A' ? '#25D366' : '#F44336';
  const rightColor = rightTeam === 'A' ? '#25D366' : '#F44336';

  const minSwipeDistance = 50;

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 390,
    h: typeof window !== 'undefined' ? window.innerHeight : 844
  }));

  useEffect(() => {
    const updateViewport = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  const isMobileView = viewport.w < 900;
  const isLandscape = viewport.w >= viewport.h;
  const mobileFullscreen = isFullscreen && isMobileView;
  const scoreFontSize = !isFullscreen
    ? { xs: 80, md: 180, lg: 240 }
    : mobileFullscreen
      ? (isLandscape ? 'min(42vw, 68dvh)' : 'min(40vw, 32dvh)')
      : { xs: 180, md: 200, lg: 350 };
  const scoreMinWidth = mobileFullscreen ? 'auto' : (isFullscreen ? { xs: 180, md: 200, lg: 350 } : { xs: 100, md: 180, lg: 240 });
  const scoreMaxWidth = mobileFullscreen ? 'none' : (isFullscreen ? { xs: 220, md: 300, lg: 500 } : { xs: 140, md: 240, lg: 320 });
  const teamNameFontSize = !isFullscreen
    ? { xs: 36, md: 64 }
    : mobileFullscreen
      ? (isLandscape ? 'clamp(18px, 3.6vw, 32px)' : 'clamp(16px, 4.6vw, 28px)')
      : { xs: 32, md: 48, lg: 80 };

  // Fullscreen toggle function
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        }).catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (isFullscreen) {
      e.preventDefault();
    }
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchEnd = (team: 'A' | 'B') => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart.y - touchEnd.y;
    const isSwipeUp = distance > minSwipeDistance;
    const isSwipeDown = distance < -minSwipeDistance;

    if (isSwipeUp && team === 'A') {
      onScoreAChange(scoreA + 1);
    } else if (isSwipeDown && team === 'A') {
      onScoreAChange(Math.max(0, scoreA - 1));
    } else if (isSwipeUp && team === 'B') {
      onScoreBChange(scoreB + 1);
    } else if (isSwipeDown && team === 'B') {
      onScoreBChange(Math.max(0, scoreB - 1));
    }
  };

  const onMouseDown = (e: React.MouseEvent, team: 'A' | 'B') => {
    setMouseEnd(null);
    setMouseStart({
      x: e.clientX,
      y: e.clientY
    });
    setIsDragging(false);
    setCurrentTeam(team);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const start = mouseStartRef.current;
    if (start) {
      setMouseEnd({
        x: e.clientX,
        y: e.clientY
      });
      // Check if moved enough to be considered dragging
      const distance = Math.abs(start.y - e.clientY);
      if (distance > 5) {
        setIsDragging(true);
      }
    }
  }, []);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    const start = mouseStartRef.current;
    const end = mouseEndRef.current;
    const team = currentTeamRef.current;
    const dragging = isDraggingRef.current;
    const admin = isAdminRef.current;
    const currentStatus = statusRef.current;
    const currentLoading = loadingRef.current;
    const currentScoreA = scoreARef.current;
    const currentScoreB = scoreBRef.current;
    
    if (!start || !end || !team) return;
    const distance = start.y - end.y;
    const isSwipeUp = distance > minSwipeDistance;
    const isSwipeDown = distance < -minSwipeDistance;

    if (isSwipeUp && team === 'A') {
      onScoreAChange(currentScoreA + 1);
    } else if (isSwipeDown && team === 'A') {
      onScoreAChange(Math.max(0, currentScoreA - 1));
    } else if (isSwipeUp && team === 'B') {
      onScoreBChange(currentScoreB + 1);
    } else if (isSwipeDown && team === 'B') {
      onScoreBChange(Math.max(0, currentScoreB - 1));
    } else if (!dragging && admin) {
      // If not a drag gesture, treat as click
      if (currentStatus !== 'Selesai' && !currentLoading) {
        if (team === 'A') {
          onScoreAChange(currentScoreA + 1);
        } else {
          onScoreBChange(currentScoreB + 1);
        }
      }
    }
    setMouseStart(null);
    setMouseEnd(null);
    setIsDragging(false);
    setCurrentTeam(null);
  }, [onScoreAChange, onScoreBChange]);

  // Add window event listeners for mouse move and up
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const scoreBoxSx = {
    fontSize: scoreFontSize,
    fontWeight: 900,
    textShadow: '2px 2px 12px #000',
    minWidth: scoreMinWidth,
    maxWidth: scoreMaxWidth,
    lineHeight: 1,
    overflow: 'visible',
    cursor: isAdmin && status !== 'Selesai' ? 'pointer' : 'default',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'none',
    WebkitTouchCallout: 'none',
    fontVariantNumeric: 'tabular-nums',
    px: mobileFullscreen ? 1 : 0,
    py: mobileFullscreen ? 1.5 : 0,
    '&:hover': isAdmin && status !== 'Selesai' && !mobileFullscreen ? {
      opacity: 0.8,
      transform: 'scale(1.05)',
      transition: 'all 0.2s ease'
    } : {}
  } as const;

  return (
    <Paper sx={{
      p: isFullscreen ? (mobileFullscreen ? 1 : 0) : { xs: 2, md: small ? 4 : 6 },
      mb: isFullscreen ? 0 : 4,
      minHeight: isFullscreen ? '100dvh' : { xs: 340, md: small ? 420 : 600 },
      height: isFullscreen ? '100dvh' : 'auto',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
      border: isFullscreen ? 'none' : (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined,
      borderRadius: isFullscreen ? 0 : 3,
      boxShadow: isFullscreen ? 'none' : 3,
      minWidth: isFullscreen ? '100vw' : { xs: 240, md: small ? 520 : 600, lg: small ? 700 : 800 },
      maxWidth: isFullscreen ? '100vw' : { xs: '100%', md: small ? 650 : 800, lg: small ? 800 : 1000 },
      overflow: 'hidden',
      overscrollBehavior: 'none',
      touchAction: isFullscreen ? 'none' : 'auto',
      position: isFullscreen ? 'fixed' : 'relative',
      top: isFullscreen ? 0 : 'auto',
      left: isFullscreen ? 0 : 'auto',
      zIndex: isFullscreen ? 9999 : 'auto',
      '@keyframes pulse': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.5 },
        '100%': { opacity: 1 },
      },
    }}>
      {!isFullscreen && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={600} align="center" sx={{ color: titleColor }}>
              Live Score Lapang {court}
            </Typography>
            {live && status === 'Berlangsung' && (
              <Chip 
                label="LIVE" 
                color="error"
                size="small"
                sx={{ animation: 'pulse 2s infinite' }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={isAdmin ? "Admin Mode" : "View Only"} 
              color={isAdmin ? "success" : "default"}
              size="small"
            />
            <IconButton 
              onClick={toggleFullscreen} 
              size="small"
              sx={{ 
                background: theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.1)' : 'rgba(25,118,210,0.1)',
                '&:hover': {
                  background: theme.palette.mode === 'dark' ? 'rgba(99,170,255,0.2)' : 'rgba(25,118,210,0.2)'
                }
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
        </Box>
      )}
      {isFullscreen && (
        <>
          <Box sx={{ position: 'absolute', top: { xs: 8, md: 16 }, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
            <Typography 
              sx={{ 
                fontSize: mobileFullscreen ? 14 : { xs: 20, md: 24, lg: 32 }, 
                fontWeight: 700, 
                color: '#fff', 
                textShadow: '2px 2px 8px #000',
                background: 'rgba(0,0,0,0.5)',
                px: { xs: 1.5, md: 3 },
                py: { xs: 0.25, md: 1 },
                borderRadius: 2
              }}
            >
              LAPANG {court}
            </Typography>
          </Box>
          <Box sx={{ position: 'absolute', top: { xs: 16, md: 16 }, right: { xs: 8, md: 16 }, zIndex: 10 }}>
            <IconButton 
              onClick={toggleFullscreen} 
              size="small"
              sx={{ 
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                '&:hover': {
                  background: 'rgba(0,0,0,0.7)'
                }
              }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </Box>
        </>
      )}
      {!isFullscreen && status === 'Selesai' && (
        <Alert severity="success" sx={{ mb: 2, fontWeight: 600 }}>
          Pertandingan Selesai
        </Alert>
      )}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', alignItems: 'center', pt: isFullscreen ? (mobileFullscreen ? (isLandscape ? 3 : 5) : { xs: 4, md: 0 }) : 0 }}>
        {!live ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
            {isAdmin ? (
              <>
                <Box sx={{ display: 'flex', gap: 3, width: { xs: '100%', md: 600 }, maxWidth: '100%', mb: 3 }}>
                  <Autocomplete
                    fullWidth
                    options={playerNames || []}
                    loading={loadingPlayerNames}
                    value={teamA}
                    onChange={(event, newValue) => {
                      setTeamA(newValue || '');
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Team A" 
                        size="medium" 
                        sx={{ flex: 1, fontSize: { xs: 24, md: 32 } }}
                      />
                    )}
                    noOptionsText="Tidak ada nama yang cocok"
                    loadingText="Memuat nama pemain..."
                    freeSolo={false} // 🚀 NEW: Prevent manual input, only allow selection from dropdown
                  />
                  <Autocomplete
                    fullWidth
                    options={playerNames || []}
                    loading={loadingPlayerNames}
                    value={teamB}
                    onChange={(event, newValue) => {
                      setTeamB(newValue || '');
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Team B" 
                        size="medium" 
                        sx={{ flex: 1, fontSize: { xs: 24, md: 32 } }}
                      />
                    )}
                    noOptionsText="Tidak ada nama yang cocok"
                    loadingText="Memuat nama pemain..."
                    freeSolo={false} // 🚀 NEW: Prevent manual input, only allow selection from dropdown
                  />
                </Box>
                <Button 
                  variant="contained" 
                  onClick={onStart} 
                  disabled={!teamA || !teamB || loading} 
                  sx={{ fontSize: { xs: 20, md: 28 }, px: 6, py: 2, mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'MULAI LIVE'}
                </Button>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                {loadingLiveScore ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary" align="center">
                      Memuat live score...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 2 }}>
                      {isAdmin ? 'Masukkan nama tim untuk memulai live score' : 'Live score akan muncul di sini saat pertandingan dimulai'}
                    </Typography>
                    {!isAdmin && (
                      <Typography variant="body2" color="text.secondary" align="center">
                        Auto-refresh setiap 5 detik untuk update real-time
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: isFullscreen ? (mobileFullscreen ? 1 : { xs: 2, md: small ? 6 : 8 }) : small ? 6 : 8,
            width: '100%',
            height: '100%',
            justifyContent: 'center',
          }}>
            {!small ? (
              // Layout untuk 1 lapang: nama di atas, skor kiri-kanan
              <>
                {/* Nama Tim di atas */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: isFullscreen ? (mobileFullscreen ? 0.5 : { xs: 1, md: 8 }) : 4, px: mobileFullscreen ? 1 : 0 }}>
                  <Typography sx={{ fontSize: teamNameFontSize, fontWeight: 800, color: '#2196F3', textShadow: '2px 2px 8px #000', flex: 1, textAlign: 'center', whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: mobileFullscreen ? '2.4em' : 'none' }}>
                    {renderTeamName(leftTeamName)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx: isFullscreen ? (mobileFullscreen ? 1 : { xs: 2, md: 8 }) : 4 }}>
                    <Typography sx={{ fontSize: mobileFullscreen ? (isLandscape ? 16 : 18) : (isFullscreen ? { xs: 24, md: 36, lg: 48 } : { xs: 24, md: 36 }), fontWeight: 600, color: '#fff', textShadow: '1px 1px 4px #000' }}>
                      VS
                    </Typography>
                    <IconButton 
                      onClick={() => setIsSwapped(!isSwapped)}
                      size="small"
                      sx={{ 
                        background: isSwapped ? 'rgba(33,150,243,0.3)' : 'rgba(255,255,255,0.1)',
                        color: isSwapped ? '#2196F3' : '#fff',
                        mt: mobileFullscreen ? 0.25 : 1,
                        '&:hover': {
                          background: isSwapped ? 'rgba(33,150,243,0.5)' : 'rgba(255,255,255,0.2)'
                        }
                      }}
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  </Box>
                  <Typography sx={{ fontSize: teamNameFontSize, fontWeight: 800, color: '#2196F3', textShadow: '2px 2px 8px #000', flex: 1, textAlign: 'center', whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: mobileFullscreen ? '2.4em' : 'none' }}>
                    {renderTeamName(rightTeamName)}
                  </Typography>
                </Box>
                {/* Skor kiri-kanan */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: isFullscreen ? (mobileFullscreen ? 0.5 : { xs: 2, md: 8 }) : 4, px: mobileFullscreen ? 0.5 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
                    <Typography 
                      sx={{ 
                        ...scoreBoxSx,
                        color: leftColor,
                        textAlign: 'center',
                      }}
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={() => onTouchEnd(leftTeam)}
                      onMouseDown={(e) => onMouseDown(e, leftTeam)}
                    >
                      {leftScore}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: mobileFullscreen ? (isLandscape ? '8vw' : '10vw') : (isFullscreen ? { xs: 80, md: 80, lg: 120 } : { xs: 36, md: 60, lg: 80 }), fontWeight: 700, color: '#fff', textShadow: '2px 2px 8px #000', flexShrink: 0, pointerEvents: 'none' }}>
                    -
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
                    <Typography 
                      sx={{ 
                        ...scoreBoxSx,
                        color: rightColor,
                        textAlign: 'center',
                      }}
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={() => onTouchEnd(rightTeam)}
                      onMouseDown={(e) => onMouseDown(e, rightTeam)}
                    >
                      {rightScore}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              // Layout untuk 2 lapang: nama kiri, skor kanan (layout lama)
              <>
                {/* Tim Kiri */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 0 }}>
                  <Typography sx={{ fontSize: { xs: 32, md: 72 }, fontWeight: 800, color: '#2196F3', textShadow: '2px 2px 8px #000', flex: 2, textAlign: 'left', pr: { xs: 2, md: 6 }, whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {renderTeamName(leftTeamName)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 } }}>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: 70, md: 140, lg: 180 }, 
                        fontWeight: 900, 
                        color: leftColor, 
                        textShadow: '2px 2px 12px #000', 
                        minWidth: { xs: 80, md: 140, lg: 180 }, 
                        maxWidth: { xs: 120, md: 200, lg: 260 }, 
                        textAlign: 'right', 
                        lineHeight: 1, 
                        overflow: 'visible',
                        cursor: isAdmin && status !== 'Selesai' ? 'pointer' : 'default',
                        userSelect: 'none',
                        '&:hover': isAdmin && status !== 'Selesai' ? {
                          opacity: 0.8,
                          transform: 'scale(1.05)',
                          transition: 'all 0.2s ease'
                        } : {}
                      }}
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={() => onTouchEnd(leftTeam)}
                      onMouseDown={(e) => onMouseDown(e, leftTeam)}
                    >
                      {leftScore}
                    </Typography>
                  </Box>
                </Box>
                {/* Tim Kanan */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontSize: { xs: 32, md: 72 }, fontWeight: 800, color: '#2196F3', textShadow: '2px 2px 8px #000', flex: 2, textAlign: 'left', pr: { xs: 2, md: 6 }, whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {renderTeamName(rightTeamName)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 } }}>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: 70, md: 140, lg: 180 }, 
                        fontWeight: 900, 
                        color: rightColor, 
                        textShadow: '2px 2px 12px #000', 
                        minWidth: { xs: 80, md: 140, lg: 180 }, 
                        maxWidth: { xs: 120, md: 200, lg: 260 }, 
                        textAlign: 'right', 
                        lineHeight: 1, 
                        overflow: 'visible',
                        cursor: isAdmin && status !== 'Selesai' ? 'pointer' : 'default',
                        userSelect: 'none',
                        '&:hover': isAdmin && status !== 'Selesai' ? {
                          opacity: 0.8,
                          transform: 'scale(1.05)',
                          transition: 'all 0.2s ease'
                        } : {}
                      }}
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={() => onTouchEnd(rightTeam)}
                      onMouseDown={(e) => onMouseDown(e, rightTeam)}
                    >
                      {rightScore}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
            {/* Tombol Simpan dan Reset - Hanya untuk Admin */}
            {/* Tombol SIMPAN hanya untuk mengubah status menjadi "Selesai" dan menampilkan ke riwayat */}
            {!isFullscreen && isAdmin && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={onSave} 
                  disabled={loading}
                  sx={{ px: 4, py: 1.5, fontSize: 16, fontWeight: 600 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'SIMPAN'}
                </Button>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  onClick={onReset} 
                  disabled={loading}
                  sx={{ px: 4, py: 1.5, fontSize: 16, fontWeight: 600 }}
                >
                  RESET
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Helper untuk render nama tim dengan split '/'
const renderTeamName = (name: string) => {
  return name.split('/').map((n, idx) => (
    <span key={idx} style={{ display: 'block' }}>{n.trim()}</span>
  ));
};

// Helper untuk generate 5-digit random ID
const generateMatchId = (): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 5; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// 🚀 NEW: Helper function untuk validation
const isValidTeamName = (name: string): boolean => {
  if (!name) return false;
  
  const trimmedName = name.trim();
  if (trimmedName.length === 0) return false;
  
  // Minimum length check (minimal 2 karakter)
  if (trimmedName.length < 2) return false;
  
  // Maximum length check (maksimal 50 karakter)
  if (trimmedName.length > 50) return false;
  
  // Check if name contains only whitespace or special characters
  const hasValidContent = /[a-zA-Z0-9\u00C0-\u017F]/.test(trimmedName);
  
  // Check if name contains invalid characters (only allow letters, numbers, spaces, and common punctuation)
  const hasInvalidChars = /[^a-zA-Z0-9\u00C0-\u017F\s\-/.()&']/g.test(trimmedName);
  
  return hasValidContent && !hasInvalidChars;
};

const UpdateScore: React.FC = () => {
  const [projectorMode, setProjectorMode] = useState(false); // Default to grid mode
  const [courtCount, setCourtCount] = useState<1 | 2>(1);
  const [selectedCourts, setSelectedCourts] = useState<number[]>([1]); // Selected courts to display
  const [loading, setLoading] = useState(false);
  const [loadingLiveScore, setLoadingLiveScore] = useState(false);
  const [history, setHistory] = useState<MatchScore[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // 🚀 NEW: Error state untuk validation
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState(false);

  const [currentAdminEmail, setCurrentAdminEmail] = useState<string | null>(null);
  
  // 🚀 NEW: Player names for autocomplete dropdown
  const [playerNames, setPlayerNames] = useState<string[]>([]);
  const [loadingPlayerNames, setLoadingPlayerNames] = useState(false);

  // Detect mobile and orientation
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
    };

    const checkOrientation = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsPortrait(height > width);
    };

    checkMobile();
    checkOrientation();

    window.addEventListener('resize', checkMobile);
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Dynamic state for 10 lapangan
  const [courts, setCourts] = useState<CourtData[]>(
    Array.from({ length: 10 }, (_, i) => ({
      teamA: '',
      teamB: '',
      scoreA: 0,
      scoreB: 0,
      live: false,
      status: 'Berlangsung',
      matchId: undefined,
      lockedBy: null
    }))
  );
  const courtsRef = useRef(courts);
  const scoreUpdateQueueRef = useRef<Record<number, Promise<void>>>({});

  useEffect(() => {
    courtsRef.current = courts;
  }, [courts]);

  // Check authentication and admin status
  // Admin biasa dan superadmin bisa menggunakan live score (ganti skor)
  useEffect(() => {
    // Refresh cache admin access saat component mount untuk memastikan data terbaru
    fetchAdminAccessData().catch(err => console.error('Error refreshing admin cache:', err));
    
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user && user.email) {
        const userEmail = user.email; // Simpan di variable untuk type safety
        // Gunakan setTimeout kecil untuk memastikan cache sudah ter-load
        setTimeout(() => {
          const isAdminUser = isAdminOrSuperAdmin(userEmail);
          console.log('UpdateScore - Admin check:', { 
            email: userEmail, 
            isAdminUser,
            normalizedEmail: userEmail.trim().toLowerCase()
          });
          if (isAdminUser) {
            setIsAdmin(true);
            setCurrentAdminEmail(userEmail);
          } else {
            setIsAdmin(false);
            setCurrentAdminEmail(null);
          }
        }, 100);
      } else {
        setIsAdmin(false);
        setCurrentAdminEmail(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // 🚀 NEW: Fetch player names from tournament spreadsheet
  useEffect(() => {
    const fetchPlayerNames = async () => {
      setLoadingPlayerNames(true);
      try {
        const response = await fetch('/api/bookings?action=tournament-player-names');
        if (response.ok) {
          const data = await response.json();
          setPlayerNames(data || []);
          console.log('Loaded player names for autocomplete:', data?.length || 0);
        } else {
          console.error('Failed to fetch player names');
          setPlayerNames([]);
        }
      } catch (error) {
        console.error('Error fetching player names:', error);
        setPlayerNames([]);
      } finally {
        setLoadingPlayerNames(false);
      }
    };

    fetchPlayerNames();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch('/api/bookings?action=score');
      if (response.ok) {
        const data = await response.json();
        // Filter only completed matches for history
        const completedMatches = (data.data || []).filter((match: MatchScore) => match.status === 'Selesai');
        setHistory(completedMatches);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  // Fungsi untuk mengambil data live score yang sedang berlangsung
  // dibungkus useCallback agar bisa menjadi dependency effect.
  const fetchLiveScore = React.useCallback(async () => {
    setLoadingLiveScore(true);
    try {
      const response = await fetch('/api/bookings?action=score');
      if (response.ok) {
        const data = await response.json();
        const liveMatches: MatchScore[] = data.data || [];
        
        console.log('Fetching live score, matches found:', liveMatches.length);
        
        setCourts(prevCourts => {
          const newCourts = [...prevCourts];
          for (let i = 0; i < 10; i++) {
            const courtNum = i + 1;
            const prev = prevCourts[i];
            const prevMatchId = (prev.matchId || '').trim();
            const matchById = prevMatchId
              ? liveMatches.find((match) => (match.matchId || '').trim() === prevMatchId)
              : undefined;
            const ongoingOnCourt = liveMatches.filter((match) =>
              String(match.lapang ?? '').trim() === String(courtNum) && match.status === 'Berlangsung'
            );
            const liveMatch = matchById || ongoingOnCourt[0];

            if (liveMatch) {
              const sameMatch = (liveMatch.matchId || '').trim() === prevMatchId;
              const keepLocalScores = isAdmin && prev.live && sameMatch;
              newCourts[i] = {
                ...prev,
                live: liveMatch.status === 'Berlangsung',
                teamA: liveMatch.teamA || '',
                teamB: liveMatch.teamB || '',
                status: liveMatch.status || 'Berlangsung',
                matchId: liveMatch.matchId,
                scoreA: keepLocalScores ? prev.scoreA : (parseInt(liveMatch.scoreA as any) || 0),
                scoreB: keepLocalScores ? prev.scoreB : (parseInt(liveMatch.scoreB as any) || 0)
              };
            } else if (isAdmin && prev.live && prevMatchId) {
              newCourts[i] = prev;
            } else {
              const isCourtLive = prev.live;
              newCourts[i] = {
                ...prev,
                live: false,
                teamA: (isAdmin && !isCourtLive) ? prev.teamA : '',
                teamB: (isAdmin && !isCourtLive) ? prev.teamB : '',
                scoreA: 0,
                scoreB: 0,
                status: 'Berlangsung',
                matchId: undefined
              };
            }
          }
          courtsRef.current = newCourts;
          return newCourts;
        });
      }
    } catch (error) {
      console.error('Error fetching live score:', error);
    } finally {
      setLoadingLiveScore(false);
    }
  }, [isAdmin]);

  // Effect untuk mengambil data awal
  useEffect(() => {
    fetchLiveScore(); // Ambil data awal satu kali
    // Don't fetch history on initial load - only when needed
  }, [fetchLiveScore]); // depend pada callback

  // Auto-refresh untuk user non-admin dan admin saat pertama login
  // Admin tidak auto-refresh saat sedang mengetik nama tim agar tidak kereset
  useEffect(() => {
    if (isAdmin) {
      // Admin: fetch sekali saat login/become admin untuk binding matchId
      fetchLiveScore();
      return;
    }

    // untuk normal user, lakukan interval 5 detik agar tidak terlalu cepat
    const interval = setInterval(() => {
      fetchLiveScore();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAdmin, fetchLiveScore]);

  // Fungsi untuk mengecek apakah admin dapat mengakses lapang tertentu
  const canAccessCourt = (courtNumber: number): boolean => {
    if (!isAdmin) return false;
    
    const courtData = courts[courtNumber - 1];
    const isLive = courtData.live;
    const lockedBy = courtData.lockedBy;
    
    // Jika lapang tidak live, admin dapat mengakses
    if (!isLive) return true;
    
    // Jika lapang live, hanya admin yang mengunci yang dapat mengakses
    return lockedBy === currentAdminEmail;
  };


  // Dynamic handler untuk start live pada lapang tertentu
  const handleStartLive = async (courtNumber: number) => {
    const courtData = courts[courtNumber - 1];
    
    // Clear previous errors
    setShowError(false);
    setErrorMessage('');
    
    // Better validation dengan error message
    if (!isValidTeamName(courtData.teamA)) {
      if (!courtData.teamA || courtData.teamA.trim().length === 0) {
        setErrorMessage('Nama Team A wajib diisi!');
      } else if (courtData.teamA.trim().length < 2) {
        setErrorMessage('Nama Team A minimal 2 karakter!');
      } else if (courtData.teamA.trim().length > 50) {
        setErrorMessage('Nama Team A maksimal 50 karakter!');
      } else {
        setErrorMessage('Nama Team A mengandung karakter yang tidak valid!');
      }
      setShowError(true);
      return;
    }
    
    if (!isValidTeamName(courtData.teamB)) {
      if (!courtData.teamB || courtData.teamB.trim().length === 0) {
        setErrorMessage('Nama Team B wajib diisi!');
      } else if (courtData.teamB.trim().length < 2) {
        setErrorMessage('Nama Team B minimal 2 karakter!');
      } else if (courtData.teamB.trim().length > 50) {
        setErrorMessage('Nama Team B maksimal 50 karakter!');
      } else {
        setErrorMessage('Nama Team B mengandung karakter yang tidak valid!');
      }
      setShowError(true);
      return;
    }
    
    if (!canAccessCourt(courtNumber)) {
      setErrorMessage(`Lapang ${courtNumber} sedang digunakan oleh admin lain. Silakan pilih lapang yang berbeda.`);
      setShowError(true);
      return;
    }
    
    // Generate unique match ID
    const newMatchId = generateMatchId();
    
    console.log(`Starting live for lapang ${courtNumber} with matchId: ${newMatchId}`);
    
    setLoading(true);
    try {
      const response = await fetch('/api/bookings?action=score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          create: true,
          lapang: courtNumber,
          teamA: courtData.teamA,
          teamB: courtData.teamB,
          scoreA: 0,
          scoreB: 0,
          status: 'Berlangsung',
          matchId: newMatchId
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        const createdMatchId = result.matchId || newMatchId;
        console.log('Match created successfully:', result);
        setCourts(prevCourts => {
          const newCourts = [...prevCourts];
          newCourts[courtNumber - 1] = {
            ...newCourts[courtNumber - 1],
            live: true,
            scoreA: 0,
            scoreB: 0,
            status: 'Berlangsung',
            matchId: createdMatchId,
            lockedBy: currentAdminEmail
          };
          courtsRef.current = newCourts;
          return newCourts;
        });
        // Update history from live score data instead of separate fetch
        const historyResponse = await fetch('/api/bookings?action=score');
        if (historyResponse.ok) {
          const data = await historyResponse.json();
          const completedMatches = (data.data || []).filter((match: MatchScore) => match.status === 'Selesai');
          setHistory(completedMatches);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setErrorMessage(errorData.message || 'Gagal memulai live score. Coba lagi.');
        setShowError(true);
      }
    } catch (error) {
      console.error(`Error starting live ${courtNumber}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic handler untuk save pada lapang tertentu
  const handleSave = async (courtNumber: number) => {
    if (!isAdmin) return;
    const courtData = courts[courtNumber - 1];
    if (!courtData.live || !courtData.matchId) return;
    setLoading(true);
    try {
      const response = await fetch('/api/bookings?action=score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchId: courtData.matchId,
          lapang: courtNumber,
          teamA: courtData.teamA,
          teamB: courtData.teamB,
          scoreA: courtData.scoreA,
          scoreB: courtData.scoreB,
          status: 'Selesai'
        })
      });
      
      if (response.ok) {
        setCourts(prevCourts => {
          const newCourts = [...prevCourts];
          newCourts[courtNumber - 1].status = 'Selesai';
          return newCourts;
        });
        // Update history from live score data instead of separate fetch
        const historyResponse = await fetch('/api/bookings?action=score');
        if (historyResponse.ok) {
          const data = await historyResponse.json();
          const completedMatches = (data.data || []).filter((match: MatchScore) => match.status === 'Selesai');
          setHistory(completedMatches);
        }
      }
    } catch (error) {
      console.error(`Error saving match ${courtNumber}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic handler untuk reset pada lapang tertentu
  const handleReset = async (courtNumber: number) => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      setCourts(prevCourts => {
        const newCourts = [...prevCourts];
        newCourts[courtNumber - 1] = {
          ...newCourts[courtNumber - 1],
          live: false,
          teamA: '',
          teamB: '',
          scoreA: 0,
          scoreB: 0,
          status: 'Berlangsung',
          matchId: undefined,
          lockedBy: null
        };
        courtsRef.current = newCourts;
        return newCourts;
      });
    } catch (error) {
      console.error(`Error resetting match ${courtNumber}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk update score otomatis ke Google Sheets setiap kali tombol + atau - ditekan
  // Score akan langsung tersimpan ke Google Sheets tanpa perlu menekan tombol SIMPAN
  const updateScoreToSheet = async (court: number) => {
    if (!isAdmin) return;
    
    const run = async () => {
      const courtData = courtsRef.current[court - 1];
      const matchId = courtData?.matchId;
      if (!matchId) {
        console.error(`Cannot update score for court ${court}: No matchId found`);
        return;
      }

      const latestScoreA = courtData.scoreA;
      const latestScoreB = courtData.scoreB;
      console.log(`Updating score for court ${court} with matchId ${matchId}: ${latestScoreA}-${latestScoreB}`);

      try {
        const response = await fetch('/api/bookings?action=score', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            matchId,
            lapang: court,
            teamA: courtData.teamA,
            teamB: courtData.teamB,
            scoreA: latestScoreA,
            scoreB: latestScoreB,
            status: courtData.status
          })
        });

        if (response.ok) {
          console.log(`Score updated successfully for court ${court}: ${latestScoreA}-${latestScoreB}`);
        } else {
          const errorData = await response.json();
          console.error(`Failed to update score for court ${court}:`, errorData);
        }
      } catch (error) {
        console.error(`Error updating score for court ${court}:`, error);
      }
    };

    const previous = scoreUpdateQueueRef.current[court] || Promise.resolve();
    const next = previous.then(run, run);
    scoreUpdateQueueRef.current[court] = next;
    await next;
  };

  // Dynamic handler untuk update score pada lapang tertentu
  const handleScoreChange = async (courtNumber: number, team: 'A' | 'B', newScore: number) => {
    if (!isAdmin) return;

    setCourts(prevCourts => {
      const newCourts = [...prevCourts];
      const updated = { ...newCourts[courtNumber - 1] };
      if (team === 'A') {
        updated.scoreA = newScore;
      } else {
        updated.scoreB = newScore;
      }
      newCourts[courtNumber - 1] = updated;
      courtsRef.current = newCourts;
      return newCourts;
    });

    await updateScoreToSheet(courtNumber);
  };

  // Helper untuk set team name
  const handleTeamNameChange = (courtNumber: number, team: 'A' | 'B', value: string) => {
    setCourts(prevCourts => {
      const newCourts = [...prevCourts];
      if (team === 'A') {
        newCourts[courtNumber - 1].teamA = value;
      } else {
        newCourts[courtNumber - 1].teamB = value;
      }
      return newCourts;
    });
  };

  const handleDeleteHistory = async (matchId: string) => {
    if (!isAdmin) return;
    if (!window.confirm('Yakin ingin menghapus riwayat pertandingan ini?')) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/bookings?action=score', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchId })
      });
      
      if (response.ok) {
        // Update history from live score data instead of separate fetch
        const historyResponse = await fetch('/api/bookings?action=score');
        if (historyResponse.ok) {
          const data = await historyResponse.json();
          const completedMatches = (data.data || []).filter((match: MatchScore) => match.status === 'Selesai');
          setHistory(completedMatches);
        }
      }
    } catch (error) {
      console.error('Error deleting history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show rotation prompt for admin on mobile portrait mode
  if (isAdmin && isMobile && isPortrait) {
    return (
      <Box 
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #0a1626 0%, #1a237e 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        <Box sx={{ textAlign: 'center', color: '#fff' }}>
          <Box 
            sx={{
              fontSize: 120,
              mb: 4,
              animation: 'rotate 2s ease-in-out infinite',
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '25%': { transform: 'rotate(-90deg)' },
                '75%': { transform: 'rotate(-90deg)' },
                '100%': { transform: 'rotate(0deg)' }
              }
            }}
          >
            📱
          </Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            Rotate Your Device
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8 }}>
            Please rotate to landscape mode for better experience
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: 2 }}>
      <Helmet>
        <title>Update Score - RaketerS</title>
      </Helmet>
      <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
        Update Score Pertandingan
      </Typography>
      
      {/* Status Login */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        {user ? (
          <Chip 
            label={`Logged in as: ${user.email} ${isAdmin ? '(Admin)' : '(User)'}`}
            color={isAdmin ? "success" : "default"}
            variant="outlined"
          />
        ) : (
          <Chip 
            label="Not logged in (View Only)"
            color="default"
            variant="outlined"
          />
        )}
      </Box>

      {/* 🚀 NEW: Error Alert Display */}
      {showError && errorMessage && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setShowError(false)}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Projector Mode Toggle - Only for non-admin users */}
      {!isAdmin && (
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
          <Typography fontWeight={600}>Mode:</Typography>
          <ToggleButtonGroup
            value={projectorMode}
            exclusive
            onChange={(_, v) => {
              if (v !== null) {
                setProjectorMode(v);
                // Reset to default when switching modes
                if (v) {
                  setCourtCount(1);
                  setSelectedCourts([1]);
                }
              }
            }}
            size="small"
          >
            <ToggleButton value={false}>Normal</ToggleButton>
            <ToggleButton value={true}>Proyektor</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      {/* Court Count Selector - Only for non-admin users in Projector Mode */}
      {!isAdmin && projectorMode && (
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
          <Typography fontWeight={600}>Tampilkan:</Typography>
          <ToggleButtonGroup
            value={courtCount}
            exclusive
            onChange={(_, v) => {
              if (v) {
                setCourtCount(v);
                // Reset selected courts when changing court count
                if (v === 1) {
                  setSelectedCourts([1]);
                } else {
                  setSelectedCourts([1, 2]);
                }
              }
            }}
            size="small"
          >
            <ToggleButton value={1}>1 Lapang</ToggleButton>
            <ToggleButton value={2}>2 Lapang</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      {/* Lapangan Selector - For non-admin users in Projector Mode and for Admin */}
      {(isAdmin || projectorMode) && (
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
          <Typography fontWeight={600}>Pilih Lapang:</Typography>
          {isAdmin ? (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Lapang</InputLabel>
              <Select
                value={selectedCourts[0]}
                label="Lapang"
                onChange={(e) => {
                  setSelectedCourts([Number(e.target.value)]);
                }}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map(num => (
                  <MenuItem key={num} value={num}>
                    Lapang {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            selectedCourts.map((courtNum, index) => (
              <FormControl key={index} size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Lapang {index + 1}</InputLabel>
                <Select
                  value={courtNum}
                  label={`Lapang ${index + 1}`}
                  onChange={(e) => {
                    const newSelectedCourts = [...selectedCourts];
                    newSelectedCourts[index] = Number(e.target.value);
                    setSelectedCourts(newSelectedCourts);
                  }}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map(num => (
                    <MenuItem key={num} value={num} disabled={selectedCourts.includes(num) && num !== courtNum}>
                      Lapang {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))
          )}
        </Stack>
      )}

      {/* Render courts based on mode - Admin shows 1 court, non-admin projector shows selected courts */}
      {isAdmin || projectorMode ? (
        // Projector Mode: Show selected courts with large display
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4, justifyContent: 'center' }}>
          {selectedCourts.map((courtNum) => {
            const courtData = courts[courtNum - 1];
            return (
              <LiveScoreBox
                key={courtNum}
                court={courtNum}
                teamA={courtData.teamA}
                teamB={courtData.teamB}
                scoreA={courtData.scoreA}
                scoreB={courtData.scoreB}
                setTeamA={(value) => handleTeamNameChange(courtNum, 'A', value)}
                setTeamB={(value) => handleTeamNameChange(courtNum, 'B', value)}
                onStart={() => handleStartLive(courtNum)}
                onSave={() => handleSave(courtNum)}
                onReset={() => handleReset(courtNum)}
                onScoreAChange={(newScore) => handleScoreChange(courtNum, 'A', newScore)}
                onScoreBChange={(newScore) => handleScoreChange(courtNum, 'B', newScore)}
                live={courtData.live}
                status={courtData.status}
                small={isAdmin ? false : courtCount === 2}
                loading={loading}
                loadingLiveScore={loadingLiveScore}
                isAdmin={isAdmin}
                isLocked={courtData.lockedBy !== null && courtData.lockedBy !== currentAdminEmail}
                lockedBy={courtData.lockedBy}
                currentAdminEmail={currentAdminEmail}
                playerNames={playerNames}
                loadingPlayerNames={loadingPlayerNames}
              />
            );
          })}
        </Box>
      ) : (
        // Grid Mode: Show all 10 courts with multi-row display
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}>
          {courts.map((courtData, index) => {
            const courtNum = index + 1;
            return (
              <Paper
                key={courtNum}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column',
                  width: { xs: '100%', sm: 'calc(50% - 8px)' },
                  background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined,
                  borderRadius: 2,
                  boxShadow: 1
                }}
              >
                {/* Header: Lapang number and LIVE status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 700, color: '#2196F3' }}>
                    Lapang {courtNum}
                  </Typography>
                  {courtData.live && courtData.status === 'Berlangsung' && (
                    <Chip label="LIVE" color="error" size="small" sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: 10, sm: 12 } } } />
                  )}
                </Box>
                
                {courtData.live ? (
                  <>
                    {/* Team A row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, color: '#2196F3', flex: 1 }}>
                        {courtData.teamA}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#25D366', ml: 2 }}>
                        {courtData.scoreA}
                      </Typography>
                    </Box>
                    
                    {/* Team B row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, color: '#2196F3', flex: 1 }}>
                        {courtData.teamB}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#F44336', ml: 2 }}>
                        {courtData.scoreB}
                      </Typography>
                    </Box>
                    
                    {/* Admin buttons */}
                    {isAdmin && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => handleScoreChange(courtNum, 'A', courtData.scoreA + 1)}
                          disabled={courtData.status === 'Selesai' || loading}
                          sx={{ minWidth: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, fontSize: { xs: 16, sm: 18 }, background: '#25D366', color: '#fff' }}
                        >
                          +
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleScoreChange(courtNum, 'B', courtData.scoreB + 1)}
                          disabled={courtData.status === 'Selesai' || loading}
                          sx={{ minWidth: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, fontSize: { xs: 16, sm: 18 }, background: '#F44336', color: '#fff' }}
                        >
                          +
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: 'text.secondary', py: 1 }}>
                    Tidak ada pertandingan
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Riwayat Pertandingan */}
      <Paper sx={{ p: 3, mt: 4,
        background: (theme) => theme.palette.mode === 'dark' ? '#0a1626' : '#fff',
        border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : undefined
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Riwayat Pertandingan
          </Typography>
          {!showHistory && (
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => {
                setShowHistory(true);
                fetchHistory();
              }}
            >
              Tampilkan Riwayat
            </Button>
          )}
        </Box>
        {showHistory && (
        <TableContainer sx={{background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(13,71,161,0.07) 0%, rgba(21,101,192,0.07) 100%)' : '#fff'}}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lapang</TableCell>
                <TableCell>Team A</TableCell>
                <TableCell>Score A</TableCell>
                <TableCell>Team B</TableCell>
                <TableCell>Score B</TableCell>
                <TableCell>Status</TableCell>
                {isAdmin && <TableCell>Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((match, index) => (
                <TableRow key={index}>
                  <TableCell>{match.lapang}</TableCell>
                  <TableCell>{match.teamA}</TableCell>
                  <TableCell>{match.scoreA}</TableCell>
                  <TableCell>{match.teamB}</TableCell>
                  <TableCell>{match.scoreB}</TableCell>
                  <TableCell>
                    <Chip 
                      label={match.status} 
                      color={match.status === 'Selesai' ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteHistory(match.matchId || '')}
                        disabled={loading}
                      >
                        Hapus
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>
      
    </Box>
  );
};

export default UpdateScore; 